import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';
import { workflowNavigationShapeError } from './workflow-navigation-authoring';

export interface WorkflowDashboardRemovalTarget { nodeId: string; dashboardId: string; }
export interface WorkflowDashboardRemovalPlan extends WorkflowDashboardRemovalTarget {
  dashboardLabel: string;
  hasMenuOverride: boolean;
  menuOverrideLabel: string | null;
  fingerprint: string;
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const identity = (value: unknown): value is string => typeof value === 'string' && !!value.trim() && value === value.trim();

/** Saved ownership only: no installed-source substitution, menu projection or normalization. */
export function readWorkflowDashboardRemoval(document: WorkflowDefinitionDocument, target: WorkflowDashboardRemovalTarget): WorkflowDashboardRemovalPlan {
  if (!identity(target?.nodeId) || !identity(target?.dashboardId) || !Array.isArray(document?.nodes))
    throw new Error('Choose one exact saved dashboard to remove.');
  const nodes = document.nodes.filter(node => node?.id === target.nodeId);
  if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step' || !object(nodes[0].declaration) || !Array.isArray(nodes[0].declaration.dashboards))
    throw new Error('The dashboard owner is missing, ambiguous or unavailable. Its settings are preserved.');
  const dashboards = nodes[0].declaration.dashboards;
  if (dashboards.some(dashboard => !object(dashboard) || !identity(dashboard.id)) || new Set(dashboards.map(dashboard => dashboard.id)).size !== dashboards.length)
    throw new Error('Saved dashboard identities are unavailable or ambiguous. Review them before removal.');
  const matches = dashboards.filter(dashboard => dashboard.id === target.dashboardId);
  if (matches.length !== 1 || typeof matches[0].label !== 'string' || !matches[0].label.trim())
    throw new Error('The selected saved dashboard is missing or unavailable.');
  const shapeError = workflowNavigationShapeError(document.navigation);
  if (shapeError) throw new Error('Saved Staff navigation is unavailable for exact dashboard removal. Its original settings are preserved.');
  const items = document.navigation?.items ?? [];
  const keys = items.map(item => JSON.stringify([item.nodeId, item.dashboardId]));
  if (items.some(item => !identity(item.nodeId) || !identity(item.dashboardId)) || new Set(keys).size !== keys.length)
    throw new Error('Saved Staff navigation identities are unavailable or ambiguous. Review them before removal.');
  const overrides = items.filter(item => item.nodeId === target.nodeId && item.dashboardId === target.dashboardId);
  return {
    nodeId: target.nodeId, dashboardId: target.dashboardId, dashboardLabel: matches[0].label,
    hasMenuOverride: overrides.length === 1, menuOverrideLabel: overrides.length ? overrides[0].label ?? null : null,
    fingerprint: JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference,
      target.nodeId, nodes[0].typeCode, dashboards.map(dashboard => dashboard.id), matches[0],
      Object.prototype.hasOwnProperty.call(document, 'navigation'), document.navigation]),
  };
}

/** One local mutation; every unrelated raw value and the outer format remain intact. */
export function applyWorkflowDashboardRemoval(document: WorkflowDefinitionDocument, target: WorkflowDashboardRemovalTarget, expectedFingerprint: string): WorkflowDefinitionDocument {
  const plan = readWorkflowDashboardRemoval(document, target);
  if (plan.fingerprint !== expectedFingerprint) throw new Error('The dashboard or Staff navigation changed. Review the current removal details again.');
  const next: WorkflowDefinitionDocument = {
    ...document,
    nodes: document.nodes.map(node => node.id !== plan.nodeId ? node : {
      ...node, declaration: { ...node.declaration!, dashboards: node.declaration!.dashboards!.filter(dashboard => dashboard.id !== plan.dashboardId) },
    }),
  };
  if (plan.hasMenuOverride) next.navigation = { ...document.navigation!, items: document.navigation!.items.filter(item => item.nodeId !== plan.nodeId || item.dashboardId !== plan.dashboardId) };
  return next;
}
